import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Copy, Check, Wallet, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

const WALLET_ADDRESS = "CR26yrVCmd46MqAd8rtHDCvV2Bocw7XukCAr7tUDpJDJ";

// Token information
const SUPPORTED_TOKENS = [
  { 
    symbol: "SOL", 
    name: "Solana", 
    color: "from-purple-500 to-blue-500",
    icon: "◎"
  },
  { 
    symbol: "USDC", 
    name: "USD Coin", 
    color: "from-blue-400 to-cyan-400",
    icon: "$"
  },
  { 
    symbol: "ATLAS", 
    name: "Star Atlas", 
    color: "from-cyan-400 to-emerald-400",
    icon: "✦"
  },
];

export default function SupportSection() {
  const [copied, setCopied] = useState(false);
  const [selectedToken, setSelectedToken] = useState("SOL");

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(WALLET_ADDRESS);
      setCopied(true);
      toast.success("Wallet address copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy address");
    }
  };

  const openPhantom = () => {
    window.open(`https://phantom.app/ul/send?address=${WALLET_ADDRESS}`, '_blank');
  };

  const openSolscan = () => {
    window.open(`https://solscan.io/account/${WALLET_ADDRESS}`, '_blank');
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="support-section">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 mb-4">
          <Heart className="w-8 h-8 text-red-500 animate-pulse" />
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">
            Support BugNexus
          </h1>
          <Heart className="w-8 h-8 text-red-500 animate-pulse" />
        </div>
        <div className="cyber-line mt-2 max-w-md mx-auto" />
        <p className="text-muted-foreground mt-4 font-body text-sm md:text-base max-w-2xl mx-auto">
          BugNexus is a community-driven project built with love for the Star Atlas community. 
          Your support helps us maintain servers, improve AI capabilities, and develop new features.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Main Donation Card */}
        <Card className="bg-gradient-to-br from-card via-card to-primary/5 border-border/40 overflow-hidden relative">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(to_bottom,white,transparent)]" />
          
          <CardHeader className="border-b border-border/40 bg-black/20 relative z-10">
            <CardTitle className="font-heading text-xl font-bold uppercase tracking-wider text-center flex items-center justify-center gap-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="bg-gradient-to-r from-primary via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Make a Donation
              </span>
              <Sparkles className="w-5 h-5 text-primary" />
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6 relative z-10">
            {/* Token Selection */}
            <div className="mb-6">
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-3 text-center">
                Accepted Tokens
              </p>
              <div className="flex justify-center gap-3">
                {SUPPORTED_TOKENS.map((token) => (
                  <button
                    key={token.symbol}
                    onClick={() => setSelectedToken(token.symbol)}
                    className={`px-4 py-2 rounded-sm border transition-all font-mono text-sm ${
                      selectedToken === token.symbol
                        ? `bg-gradient-to-r ${token.color} text-white border-transparent shadow-lg shadow-primary/20`
                        : "bg-black/20 border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                    data-testid={`token-${token.symbol}`}
                  >
                    <span className="mr-1">{token.icon}</span>
                    {token.symbol}
                  </button>
                ))}
              </div>
            </div>

            {/* QR Code and Wallet Address */}
            <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
              {/* QR Code */}
              <div className="flex-shrink-0">
                <div className="p-3 bg-white rounded-lg shadow-lg">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${WALLET_ADDRESS}&bgcolor=ffffff&color=000000`}
                    alt="Wallet QR Code"
                    className="w-36 h-36"
                    data-testid="qr-code"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground font-mono mt-2 text-center">
                  Scan with wallet app
                </p>
              </div>

              {/* Wallet Info */}
              <div className="flex-1 w-full">
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2">
                  Solana Wallet Address
                </p>
                <div className="bg-black/30 border border-border/50 rounded-sm p-3 flex items-center gap-2">
                  <code className="flex-1 text-xs md:text-sm font-mono text-foreground break-all">
                    {WALLET_ADDRESS}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyAddress}
                    className="flex-shrink-0"
                    data-testid="copy-address-btn"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openPhantom}
                    className="font-mono text-xs flex-1"
                    data-testid="open-phantom-btn"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Open in Phantom
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openSolscan}
                    className="font-mono text-xs flex-1"
                    data-testid="view-solscan-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View on Solscan
                  </Button>
                </div>
              </div>
            </div>

            {/* Compatible Wallets */}
            <div className="border-t border-border/40 pt-4">
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-3 text-center">
                Compatible Wallets
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-sm">
                  <span className="text-purple-400 text-lg">👻</span>
                  <span className="text-xs font-mono text-purple-300">Phantom</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-sm">
                  <span className="text-orange-400 text-lg">🔥</span>
                  <span className="text-xs font-mono text-orange-300">Solflare</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-sm">
                  <span className="text-blue-400 text-lg">💳</span>
                  <span className="text-xs font-mono text-blue-300">Any Solana Wallet</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why Support */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card className="bg-card border-border/40">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🖥️</span>
              </div>
              <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mb-2">
                Server Costs
              </h3>
              <p className="text-xs text-muted-foreground font-body">
                Help us keep the servers running 24/7 for the Star Atlas community
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/40">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mb-2">
                AI Development
              </h3>
              <p className="text-xs text-muted-foreground font-body">
                Fund improvements to bug classification and analysis AI systems
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/40">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mb-2">
                New Features
              </h3>
              <p className="text-xs text-muted-foreground font-body">
                Support development of new QA tools and community features
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Thank You Message */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground font-body italic">
            "Every contribution, no matter how small, helps make BugNexus better for everyone. 
            <br />Thank you for being part of the Star Atlas QA community!"
          </p>
          <div className="flex justify-center gap-1 mt-4">
            {[...Array(5)].map((_, i) => (
              <Heart key={`heart-${i}`} className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" style={{ animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
